import clsx from 'clsx';
import { opsThreadState } from '@/domain/opsThreadLoop';
import type { InternalDmThread } from '@/domain/types';

const HOUR = 3_600_000;

/**
 * The reply state of the Ops DM tagged for a worklist row (W5 mj): "Messaged Xh
 * ago" while awaiting, "Replied" plus the reply's snippet once someone answers.
 * `who` prefixes the pill on link rows ("Sweeper messaged 2h ago"). Rows that
 * haven't been messaged render nothing unless `showNotMessaged`.
 */
export default function OpsThreadPill({
  itemId,
  dmThreads,
  nowIso,
  who,
  showNotMessaged,
}: {
  itemId: string;
  dmThreads: Record<string, InternalDmThread>;
  nowIso: string;
  who?: string;
  showNotMessaged?: boolean;
}) {
  const t = opsThreadState(itemId, dmThreads);
  if (t.state === 'not-messaged' && !showNotMessaged) return null;

  const hoursAgo = (iso: string) => Math.max(0, Math.floor((Date.parse(nowIso) - Date.parse(iso)) / HOUR));
  const label =
    t.state === 'not-messaged'
      ? 'Not messaged'
      : t.state === 'awaiting'
        ? `${who ? `${who} messaged` : 'Messaged'} ${hoursAgo(t.sentAt) === 0 ? 'just now' : `${hoursAgo(t.sentAt)}h ago`}`
        : `${who ? `${who} replied` : 'Replied'}`;

  return (
    <span
      data-testid={`ops-thread-${itemId}`}
      title={t.state === 'replied' ? t.snippet : undefined}
      className="inline-flex items-center gap-1.5 min-w-0"
    >
      <span
        className={clsx(
          'inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-widest shrink-0',
          t.state === 'replied'
            ? 'bg-mint/15 text-mint'
            : t.state === 'awaiting'
              ? 'bg-azure/15 text-azure'
              : 'bg-cream-soft/15 text-cream-soft',
        )}
      >
        {label}
      </span>
      {t.state === 'replied' ? (
        <span className="font-serif text-[11px] normal-case tracking-normal text-cream-soft truncate max-w-[260px]">
          &ldquo;{t.snippet}&rdquo;
        </span>
      ) : null}
    </span>
  );
}
